import { useState, useEffect } from 'react';
import {
  ShieldCheck, Key, Copy, RefreshCw, Trash2, Loader2, CheckCircle,
  XCircle, Clock, BarChart3, ListFilter, Search, Code2, Eye, EyeOff
} from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../services/api';

function OtpService() {
  // ── Templates (reuse existing /api/templates, AUTHENTICATION/UTILITY preferred) ──
  const [waTemplates, setWaTemplates] = useState([]);

  // ── Config ──
  const [templateName, setTemplateName] = useState('');
  const [templateLanguage, setTemplateLanguage] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [callbackUrl, setCallbackUrl] = useState('');
  const [callbackSecret, setCallbackSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [rotatingSecret, setRotatingSecret] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  // ── API Key / Credential ──
  const [credential, setCredential] = useState(null); // { api_key_prefix, status, created_at, last_used_at }
  const [newApiKey, setNewApiKey] = useState(''); // shown only once after generation
  const [generatingKey, setGeneratingKey] = useState(false);
  const [revokingKey, setRevokingKey] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  // ── Stats ──
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  // ── Logs ──
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [logFilters, setLogFilters] = useState({ phone: '', requestId: '', status: '', purpose: '', dateFrom: '', dateTo: '' });
  const [logPage, setLogPage] = useState(1);
  const [logTotal, setLogTotal] = useState(0);

  useEffect(() => {
    loadWaTemplates();
    loadConfig();
    loadStats();
    loadLogs(1);
  }, []);

  // ==========================================
  // LOAD DATA
  // ==========================================

  const loadWaTemplates = async () => {
    try {
      const res = await API.get('/templates');
      setWaTemplates((res.data.data || []).filter(t =>
        t.status === 'APPROVED' &&
        (
          String(t.category || '').toUpperCase() === 'UTILITY' ||
          String(t.category || '').toUpperCase() === 'AUTHENTICATION' ||
          !t.category
        )
      ));
    } catch (error) {
      console.error('Load WhatsApp templates error:', error);
    }
  };

  const loadConfig = async () => {
    try {
      const res = await API.get('/otp/config');
      const { config, credential } = res.data.data;
      if (config) {
        setTemplateName(config.template_name || '');
        setTemplateLanguage(config.template_language || '');
        setIsEnabled(!!config.is_enabled);
        setCallbackUrl(config.callback_url || '');
        setCallbackSecret(config.callback_secret || '');
      }
      setCredential(credential || null);
    } catch (error) {
      console.error('Load OTP config error:', error);
    } finally {
      setConfigLoaded(true);
    }
  };

  const loadStats = async () => {
    try {
      setLoadingStats(true);
      const res = await API.get('/otp/stats');
      setStats(res.data.data);
    } catch (error) {
      console.error('Load OTP stats error:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadLogs = async (page = logPage) => {
    try {
      setLoadingLogs(true);
      const params = { page, limit: 25 };
      if (logFilters.phone) params.phone = logFilters.phone;
      if (logFilters.requestId) params.requestId = logFilters.requestId;
      if (logFilters.status) params.status = logFilters.status;
      if (logFilters.purpose) params.purpose = logFilters.purpose;
      if (logFilters.dateFrom) params.dateFrom = logFilters.dateFrom;
      if (logFilters.dateTo) params.dateTo = logFilters.dateTo;

      const res = await API.get('/otp/logs', { params });
      setLogs(res.data.data || []);
      setLogTotal(res.data.pagination?.total || 0);
      setLogPage(page);
    } catch (error) {
      console.error('Load OTP logs error:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  // ==========================================
  // ACTIONS
  // ==========================================

  const handleTemplateSelect = (id) => {
    const t = waTemplates.find(t => String(t.id) === String(id));
    if (t) {
      setTemplateName(t.template_name);
      setTemplateLanguage(t.language_code);
    } else {
      setTemplateName('');
      setTemplateLanguage('');
    }
  };

  const handleSaveConfig = async () => {
    if (!templateName) {
      toast.error('Please select a template');
      return;
    }
    const toastId = toast.loading('Saving configuration...');
    setSavingConfig(true);
    try {
      await API.post('/otp/config', {
        templateName,
        templateLanguage,
        isEnabled,
        callbackUrl: callbackUrl.trim()
      });
      toast.success('OTP configuration saved', { id: toastId });
      loadConfig();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save configuration', { id: toastId });
    } finally {
      setSavingConfig(false);
    }
  };

  const handleGenerateKey = async () => {
    if (credential && !window.confirm('Regenerating will immediately disable the current key. Any system using it will start getting 401 errors. Continue?')) return;
    const toastId = toast.loading('Generating new API key...');
    setGeneratingKey(true);
    try {
      const res = await API.post('/otp/generate-key');
      setNewApiKey(res.data.data.apiKey);
      toast.success('New API key generated', { id: toastId });
      loadConfig(); // refresh credential preview (prefix/status)
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate API key', { id: toastId });
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleRevokeKey = async () => {
    if (!confirmRevoke) {
      setConfirmRevoke(true);
      return;
    }
    const toastId = toast.loading('Revoking API key...');
    setRevokingKey(true);
    try {
      await API.delete('/otp/revoke-key');
      toast.success('API key revoked', { id: toastId });
      setNewApiKey('');
      setConfirmRevoke(false);
      loadConfig();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to revoke API key', { id: toastId });
    } finally {
      setRevokingKey(false);
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(newApiKey);
    toast.success('API key copied to clipboard');
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(callbackSecret);
    toast.success('Callback secret copied');
  };

  const handleRotateSecret = async () => {
    if (!window.confirm('Regenerate callback secret? Your server must be updated with the new secret, otherwise signature checks will fail.')) return;
    const toastId = toast.loading('Regenerating secret...');
    setRotatingSecret(true);
    try {
      const res = await API.post('/otp/rotate-secret');
      setCallbackSecret(res.data.data.callbackSecret);
      setShowSecret(true);
      toast.success('Callback secret regenerated', { id: toastId });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to regenerate secret', { id: toastId });
    } finally {
      setRotatingSecret(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setLogFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    loadLogs(1);
  };

  // ==========================================
  // UI HELPERS
  // ==========================================

  const statusBadge = (status) => {
    const map = {
      sent: { bg: '#dbeafe', color: '#1e40af', icon: <CheckCircle size={12} />, label: 'Sent' },
      delivered: { bg: '#dcfce7', color: '#166534', icon: <CheckCircle size={12} />, label: 'Delivered' },
      read: { bg: '#ede9fe', color: '#6d28d9', icon: <CheckCircle size={12} />, label: 'Read' },
      failed: { bg: '#fee2e2', color: '#991b1b', icon: <XCircle size={12} />, label: 'Failed' },
    };
    const s = map[status] || { bg: '#f1f5f9', color: '#64748b', icon: <Clock size={12} />, label: status || '—' };
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '3px 10px', borderRadius: '20px', fontSize: '12px',
        fontWeight: '700', background: s.bg, color: s.color
      }}>
        {s.icon} {s.label}
      </span>
    );
  };

  const statCards = stats ? [
    { label: 'Total', value: stats.total, color: '#1e3a5f' },
    { label: 'Sent', value: stats.sent, color: '#1e40af' },
    { label: 'Delivered', value: stats.delivered, color: '#166534' },
    { label: 'Failed', value: stats.failed, color: '#991b1b' },
    { label: 'Today', value: stats.today, color: '#92400e' },
    { label: 'This Month', value: stats.thisMonth, color: '#6d28d9' },
    { label: 'Success Rate', value: `${stats.successRate}%`, color: '#128C7E' },
    { label: 'Delivery Rate', value: `${stats.deliveryRate}%`, color: '#166534' },
  ] : [];

  return (
    <div className="dash-page">
      <div className="dash-header">
        <div>
          <h1 className="dash-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ShieldCheck size={26} color="#1e3a5f" /> OTP Service
          </h1>
          <p className="dash-subtitle">Send real-time WhatsApp OTPs from your own system via a simple API</p>
        </div>
      </div>

      {/* Step 1 — Configuration */}
      <div className="dash-card mb-lg">
        <h2 className="settings-tab-title" style={{ marginBottom: '6px' }}>Step 1 — Configuration</h2>
        <p className="settings-tab-subtitle" style={{ marginBottom: '16px' }}>
          Select the approved WhatsApp template to use for OTP delivery, and enable the service.
        </p>

        {waTemplates.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><Code2 size={48} color="#cbd5e1" /></div>
            <h3 className="empty-state-title">No Approved Templates</h3>
            <p className="empty-state-description">Sync approved templates from Meta in Settings → WhatsApp Configuration first.</p>
          </div>
        ) : (
          <>
            <div className="settings-form-group">
              <label className="settings-form-label">OTP Template <span className="settings-form-required">*</span></label>
              <select
                className="settings-form-input"
                value={waTemplates.find(t => t.template_name === templateName && t.language_code === templateLanguage)?.id || ''}
                onChange={(e) => handleTemplateSelect(e.target.value)}
              >
                <option value="">— Choose a template —</option>
                {waTemplates.map(t => (
                  <option key={t.id} value={t.id}>{t.template_name} ({t.language_code})</option>
                ))}
              </select>
              {templateName && !waTemplates.some(t => t.template_name === templateName && t.language_code === templateLanguage) && (
                <p className="settings-form-hint" style={{ color: '#991b1b' }}>
                  Saved template "{templateName}" is no longer available or approved. Please choose another one.
                </p>
              )}
              <p className="settings-form-hint">
                Use a simple body-only UTILITY template with exactly one variable (e.g. "Your OTP is {'{{1}}'}").
              </p>
            </div>

            <div className="settings-form-group">
              <label className="settings-form-label">Callback URL (optional)</label>
              <input
                type="text"
                className="settings-form-input"
                placeholder="https://yourserver.com/webhooks/otp-status"
                value={callbackUrl}
                onChange={(e) => setCallbackUrl(e.target.value)}
              />
              <p className="settings-form-hint">
                We'll POST a signed status update here whenever an OTP is delivered, read, or fails.
              </p>
            </div>

            {callbackSecret && (
              <div className="settings-form-group">
                <label className="settings-form-label">Callback Secret</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <code style={{
                    flex: 1, minWidth: '260px', padding: '8px 12px', background: '#f8fafc',
                    border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', wordBreak: 'break-all'
                  }}>
                    {showSecret ? callbackSecret : '•'.repeat(32)}
                  </code>
                  <button type="button" className="settings-btn-test" onClick={() => setShowSecret(s => !s)}>
                    {showSecret ? <EyeOff size={14} /> : <Eye size={14} />} {showSecret ? 'Hide' : 'Show'}
                  </button>
                  <button type="button" className="settings-btn-test" onClick={handleCopySecret}>
                    <Copy size={14} /> Copy
                  </button>
                  <button type="button" className="settings-btn-test" onClick={handleRotateSecret} disabled={rotatingSecret}>
                    {rotatingSecret ? <><Loader2 size={14} /> Working...</> : <><RefreshCw size={14} /> Regenerate</>}
                  </button>
                </div>
                <p className="settings-form-hint">
                  Use this secret on your server to verify the X-ChatTech24-Signature header on callbacks.
                </p>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
                id="otpEnabled"
              />
              <label htmlFor="otpEnabled" style={{ fontSize: '14px', color: '#475569', cursor: 'pointer' }}>
                Enable OTP Service for this account
              </label>
            </div>

            <div className="settings-actions">
              <button onClick={handleSaveConfig} disabled={savingConfig || !configLoaded} className="settings-btn-save">
                {savingConfig ? <><Loader2 size={15} /> Saving...</> : <>Save Configuration</>}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Step 2 — API Key */}
      <div className="dash-card mb-lg">
        <h2 className="settings-tab-title" style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Key size={18} /> Step 2 — API Key
        </h2>
        <p className="settings-tab-subtitle" style={{ marginBottom: '16px' }}>
          Use this key from your own server to call our OTP API. It is never shown again after generation — store it securely.
        </p>

        {newApiKey && (
          <div style={{
            padding: '14px 16px', background: '#fffbeb', border: '1px solid #fde68a',
            borderRadius: '10px', marginBottom: '16px'
          }}>
            <div style={{ fontSize: '12.5px', color: '#92400e', fontWeight: '700', marginBottom: '8px' }}>
              ⚠️ Copy this key now — it will not be shown again
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{
                flex: 1, minWidth: '260px', padding: '8px 12px', background: '#fff',
                border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px',
                wordBreak: 'break-all'
              }}>{newApiKey}</code>
              <button onClick={handleCopyKey} className="settings-btn-test" type="button">
                <Copy size={14} /> Copy
              </button>
            </div>
          </div>
        )}

        {credential ? (
          <div style={{
            padding: '12px 16px', background: '#f8fafc', border: '1px solid #e2e8f0',
            borderRadius: '10px', marginBottom: '16px', fontSize: '13.5px', color: '#475569'
          }}>
            <div><strong>Active Key:</strong> {credential.api_key_prefix}••••••••••••••••••••</div>
            <div style={{ marginTop: '4px' }}>
              <strong>Created:</strong> {new Date(credential.created_at).toLocaleString('en-IN')}
            </div>
            <div style={{ marginTop: '4px' }}>
              <strong>Last used:</strong> {credential.last_used_at ? new Date(credential.last_used_at).toLocaleString('en-IN') : 'Never'}
            </div>
          </div>
        ) : (
          <div style={{
            padding: '12px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0',
            borderRadius: '10px', marginBottom: '16px', fontSize: '13.5px', color: '#64748b'
          }}>
            No active API key yet. Generate one to start using the OTP API.
          </div>
        )}

        <div className="settings-actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={handleGenerateKey} disabled={generatingKey} className="settings-btn-save">
            {generatingKey ? <><Loader2 size={15} /> Generating...</> : <><RefreshCw size={15} /> {credential ? 'Regenerate Key' : 'Generate API Key'}</>}
          </button>

          {credential && (
            <button
              onClick={handleRevokeKey}
              disabled={revokingKey}
              className="settings-btn-test"
              type="button"
              style={confirmRevoke ? { background: '#fee2e2', color: '#991b1b', borderColor: '#fecaca' } : {}}
            >
              {revokingKey ? <><Loader2 size={15} /> Revoking...</> : <><Trash2 size={15} /> {confirmRevoke ? 'Click again to confirm' : 'Revoke Key'}</>}
            </button>
          )}
        </div>

        {confirmRevoke && (
          <p style={{ marginTop: '10px', fontSize: '12.5px', color: '#991b1b' }}>
            This will immediately disable the current key. Any client using it will start getting 401 errors until a new key is generated.
          </p>
        )}
      </div>

      {/* Integration Reference */}
      <div className="dash-card mb-lg">
        <h2 className="settings-tab-title" style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Code2 size={18} /> Integration Reference
        </h2>
        <p className="settings-tab-subtitle" style={{ marginBottom: '12px' }}>
          Call this endpoint from your own server whenever you need to send an OTP.
        </p>
        <div style={{
          padding: '14px 16px', background: '#0f172a', borderRadius: '10px',
          fontSize: '12.5px', color: '#e2e8f0', fontFamily: 'monospace',
          overflowX: 'auto', lineHeight: '1.7'
        }}>
{`POST ${API.defaults.baseURL}/v1/otp/send
Authorization: Bearer ct24_xxxxxxxxxxxxxxxxxxxx
Content-Type: application/json

{
  "to": "919876543210",
  "otp": "123456",
  "purpose": "login",
  "reference_id": "your-own-tracking-id"
}`}
        </div>
        <p className="settings-form-hint" style={{ marginTop: '10px' }}>
          Your server generates and verifies the OTP. We only handle WhatsApp delivery and status tracking.
        </p>
      </div>

      {/* Stats */}
      <div className="dash-card mb-lg">
        <h2 className="settings-tab-title" style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BarChart3 size={18} /> Statistics
        </h2>
        {loadingStats ? (
          <div className="loading-container" style={{ padding: '20px 0' }}>
            <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: '#1e3a5f' }} />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
            {statCards.map((s, i) => (
              <div key={i} style={{
                padding: '14px 16px', background: '#f8fafc', border: '1px solid #e2e8f0',
                borderRadius: '10px', textAlign: 'center'
              }}>
                <div style={{ fontSize: '20px', fontWeight: '800', color: s.color }}>{s.value}</div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Logs */}
      <div className="dash-card">
        <div className="settings-tab-header">
          <h2 className="settings-tab-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ListFilter size={18} /> OTP Logs
          </h2>
          <button onClick={() => loadLogs(logPage)} className="settings-btn-test" type="button">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <input
            type="text"
            className="settings-form-input"
            style={{ maxWidth: '200px' }}
            placeholder="Search phone"
            value={logFilters.phone}
            onChange={(e) => handleFilterChange('phone', e.target.value)}
          />
          <input
            type="text"
            className="settings-form-input"
            style={{ maxWidth: '200px' }}
            placeholder="Request ID"
            value={logFilters.requestId}
            onChange={(e) => handleFilterChange('requestId', e.target.value)}
          />
          <select
            className="settings-form-input"
            style={{ maxWidth: '160px' }}
            value={logFilters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="sent">Sent</option>
            <option value="delivered">Delivered</option>
            <option value="read">Read</option>
            <option value="failed">Failed</option>
          </select>
          <input
            type="text"
            className="settings-form-input"
            style={{ maxWidth: '160px' }}
            placeholder="Purpose"
            value={logFilters.purpose}
            onChange={(e) => handleFilterChange('purpose', e.target.value)}
          />
          <input
            type="date"
            className="settings-form-input"
            style={{ maxWidth: '160px' }}
            value={logFilters.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
          />
          <input
            type="date"
            className="settings-form-input"
            style={{ maxWidth: '160px' }}
            value={logFilters.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
          />
          <button onClick={handleApplyFilters} className="settings-btn-save" type="button">
            <Search size={14} /> Search
          </button>
        </div>

        {loadingLogs ? (
          <div className="loading-container" style={{ padding: '30px 0' }}>
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#1e3a5f' }} />
          </div>
        ) : logs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><ShieldCheck size={56} color="#cbd5e1" /></div>
            <h3 className="empty-state-title">No OTP Requests Yet</h3>
            <p className="empty-state-description">Once your server starts calling the OTP API, requests will appear here.</p>
          </div>
        ) : (
          <>
            <div className="table-container table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Request ID</th>
                    <th>Phone</th>
                    <th>Purpose</th>
                    <th>Reference ID</th>
                    <th>Status</th>
                    <th>Error</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.request_id}>
                      <td style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>{log.request_id}</td>
                      <td>{log.phone}</td>
                      <td>{log.purpose || '—'}</td>
                      <td>{log.reference_id || '—'}</td>
                      <td>{statusBadge(log.status)}</td>
                      <td style={{ color: '#dc2626', fontSize: '12.5px' }}>{log.error_message || '—'}</td>
                      <td style={{ fontSize: '12.5px', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {new Date(log.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '16px' }}>
              <button
                onClick={() => loadLogs(Math.max(1, logPage - 1))}
                disabled={logPage === 1}
                className="settings-btn-test"
                type="button"
                style={{ opacity: logPage === 1 ? 0.5 : 1 }}
              >
                ← Previous
              </button>
              <span style={{ display: 'flex', alignItems: 'center', fontSize: '13.5px', color: '#64748b' }}>Page {logPage}</span>
              <button
                onClick={() => loadLogs(logPage + 1)}
                disabled={logPage * 25 >= logTotal}
                className="settings-btn-test"
                type="button"
                style={{ opacity: logPage * 25 >= logTotal ? 0.5 : 1 }}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default OtpService;